import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import {
  Button, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, Text, useColorModeValue,
} from "@chakra-ui/react";
import { formatAmount, formatSubjectLabel, getPayoutPhone, isMobileMoneySubject } from "./transactionsManageConstants";
import { KV_ROW } from "./transactionsManageStyles";

/**
 * Formats an ISO date as DD/MM/YYYY HH:mm or "—" when missing/invalid.
 * @param {string} value - ISO date string
 * @returns {string}
 */
function formatDateTime(value) {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * PUTs /approved/<id>, surfaces toasts, triggers callbacks.
 * @param {Object} args - transaction, setLoading, onApproved, onClose
 * @returns {Promise<void>}
 */
async function submitApprove({ transaction, setLoading, onApproved, onClose }) {
  setLoading(true);
  try {
    await axios.put(`/admin/transactions/approved/${transaction.id}`);
    toast.success("Paiement validé");
    onApproved?.(transaction);
    onClose();
  } catch (error) {
    const msg = error?.response?.data || "Échec de la validation";
    toast.error(typeof msg === "string" ? msg : "Échec de la validation");
  } finally {
    setLoading(false);
  }
}

/**
 * Renders one key/value summary row inside the confirmation dialog.
 * @param {Object} props - label (string), value (node)
 * @returns {React.ReactElement}
 */
function KvRow({ label, value }) {
  const keyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const valColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  return (
    <Flex {...KV_ROW} borderBottomWidth="1px" borderColor="whiteAlpha.200">
      <Text fontSize="sm" color={keyColor}>{label}</Text>
      <Text fontSize="sm" fontWeight="600" fontFamily="mono" color={valColor} textAlign="right">{value}</Text>
    </Flex>
  );
}

/**
 * Renders the key/value summary block for a transaction.
 * @param {Object} props - transaction object
 * @returns {React.ReactElement}
 */
function TransactionSummary({ transaction }) {
  const mobileMoney = isMobileMoneySubject(transaction?.subject);
  return (
    <Flex direction="column">
      <KvRow label="ID transaction" value={`#${transaction?.id || "—"}`} />
      <KvRow label="Bénéficiaire (wallet)" value={`#${transaction?.wallet_id || "—"}`} />
      <KvRow label="Sujet" value={formatSubjectLabel(transaction?.subject)} />
      {mobileMoney ? (
        <KvRow label="Téléphone destination"
          value={getPayoutPhone(transaction) || "Non enregistré — voir profil artiste"} />
      ) : null}
      <KvRow label="Direction" value={transaction?.increase ? "Crédit (+)" : "Débit (−)"} />
      <KvRow label="Montant" value={formatAmount(transaction)} />
      <KvRow label="Créée le" value={formatDateTime(transaction?.created_at)} />
    </Flex>
  );
}

/**
 * Returns the confirmation note matching the transaction payment rail.
 * Mobile money payouts are sent manually outside the admin app, other
 * rows are handled by the Stripe background worker.
 * @param {Object} transaction - Transaction object
 * @returns {string}
 */
function getApprovalNote(transaction) {
  if (isMobileMoneySubject(transaction?.subject)) {
    return "Cette action change le statut de la transaction en « accepted ». Le paiement doit être envoyé manuellement au numéro de destination indiqué ci-dessous avant de valider.";
  }
  return "Cette action change le statut de la transaction en « accepted ». Le payout Stripe sera traité par le worker d'arrière-plan.";
}

/**
 * Confirmation modal before approving a pending transaction.
 * @param {Object} props - transaction (null = closed), onClose, onApproved
 * @returns {React.ReactElement}
 */
function ApproveTransactionDialog({ transaction, onClose, onApproved }) {
  const [loading, setLoading] = useState(false);
  const noteColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const onConfirm = () => submitApprove({ transaction, setLoading, onApproved, onClose });
  return (
    <Modal isOpen={Boolean(transaction)} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Valider ce paiement ?</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color={noteColor} mb="14px">
            {getApprovalNote(transaction)}
          </Text>
          {transaction && <TransactionSummary transaction={transaction} />}
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button colorScheme="green" onClick={onConfirm} isLoading={loading}>Valider le paiement</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ApproveTransactionDialog;
